import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 4.1 — AIPlaylistBuilderDialog
 * Modal-style "Create Playlist with AI" flow.
 * Inputs: vibe (required), mood (optional), energy (optional), genre (optional).
 * Backend names + tags + auto-fills tracks. We then navigate to the new playlist page.
 */
const ENERGIES = ["Chill", "Mid", "Hype"];

export const AIPlaylistBuilderDialog = ({ trigger, defaultGenre = "" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [vibe, setVibe] = useState("");
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [genre, setGenre] = useState(defaultGenre);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!vibe.trim()) {
      toast.error("Describe the vibe to generate");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/playlists/ai-generate`, {
        vibe: vibe.trim(),
        mood: mood.trim() || undefined,
        energy: energy.trim() || undefined,
        genre: genre.trim() || undefined,
        track_limit: 12,
      });
      toast.success(`Playlist "${r.data.name}" created!`, {
        description: `${r.data.track_count} tracks added`,
      });
      setOpen(false);
      setVibe(""); setMood(""); setEnergy(""); setGenre(defaultGenre);
      navigate(`/playlist/${r.data.playlist_id}`);
    } catch (e) {
      toast.error("Couldn't generate playlist", {
        description: e?.response?.data?.detail || "Try again",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            data-testid="ai-playlist-trigger"
            className="bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold whitespace-normal text-xs sm:text-sm"
          >
            ✨ Create Playlist with AI
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#13111c] border-fuchsia-500/40 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <span>✨</span> Create Playlist with AI
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30">Beta</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wider">Vibe / theme *</label>
            <Textarea
              data-testid="ai-playlist-vibe-input"
              value={vibe}
              onChange={(e) => setVibe(e.target.value)}
              placeholder="e.g. late-night drive through the city, heartbreak healing, summer rooftop"
              rows={2}
              className="form-textarea mt-1"
              maxLength={160}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Mood</label>
              <Input
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                placeholder="dreamy, gritty…"
                className="form-input mt-1"
                data-testid="ai-playlist-mood-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Genre</label>
              <Input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                placeholder="any"
                className="form-input mt-1"
                data-testid="ai-playlist-genre-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Energy</label>
            <div className="flex gap-2">
              {ENERGIES.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEnergy(energy === e ? "" : e)}
                  data-testid={`ai-playlist-energy-${e.toLowerCase()}`}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                    energy === e
                      ? "bg-fuchsia-500/30 border-fuchsia-500 text-white"
                      : "bg-gray-900/40 border-gray-700 text-gray-400 hover:border-fuchsia-500/40"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="text-white border-gray-700">
            Cancel
          </Button>
          <Button
            onClick={generate}
            disabled={generating || !vibe.trim()}
            data-testid="ai-playlist-generate-submit"
            className="bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold"
          >
            {generating ? (
              <>
                <span className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin mr-2" />
                Generating…
              </>
            ) : "✨ Generate Playlist"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIPlaylistBuilderDialog;
